// @ts-check

import { PurposeRestriction, RestrictionType, TCModel, TCString } from '@mol-ads/tcf-core'
import { GVL } from '@mol-ads/tcf-core'
import { createLog } from '../utils/logger'
import * as TcModelFactory from './TcModelFactory'

const log = createLog()

export class TcModel {
  /**
   * @param {TcModelOpts} opts 
   */
  constructor ({ gvl, molGvlVersion, model, specialPurposeOnlyDeclared = [] }) {
    this.gvl = gvl
    /**
     * @type {TCModel}
     */
    this.model = model
    this.molGvlVersion = molGvlVersion.replace(/\//g, '.')
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
    this.byPurposeVendorMap = this.byPurposeVendorMap.bind(this)
    this.gvlIsReady = this.gvlIsReady.bind(this)
    this.updated = this.updated.bind(this)
    this.tcString = this.tcString.bind(this)
    this.updateUserPreferences = this.updateUserPreferences.bind(this)
    this.consentAll = this.consentAll.bind(this)
    this.dissentAll = this.dissentAll.bind(this)
    this.hasUserDissentedToAll = this.hasUserDissentedToAll.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.consentedVendorCheck = this.consentedVendorCheck.bind(this)
  }

  reload (tcString) {
    const [newModel, specialPurposeOnlyDeclared] = TcModelFactory.createModelFromTcString(this.gvl, tcString)
    log('reloading tcModel', tcString, newModel)

    this.model = newModel
    this.specialPurposeOnlyDeclared = specialPurposeOnlyDeclared
  }

  byPurposeVendorMap () {
    return this.model.gvl.byPurposeVendorMap
  }

  gvlIsReady () {
    return this.model.gvl.readyPromise
  }

  updated () {
    this.model.updated()
  }

  tcString () {
    log('encoding', this.model)
    return TCString.encode(this.model)
  }

  updateUserPreferences ({ vendorConsents, vendorLegitimateInterests, purposeConsents, purposeLegitimateInterests, specialFeatureOptins, consentScreen, created, lastUpdated }) {
    this.model.vendorConsents.empty()
    this.model.vendorConsents.set(vendorConsents)

    this.model.vendorLegitimateInterests.empty()
    this.model.vendorLegitimateInterests.set(vendorLegitimateInterests)
    this.model.vendorLegitimateInterests.set(this.specialPurposeOnlyDeclared.map(({ id }) => id))

    this.model.purposeConsents.empty()
    this.model.purposeConsents.set(purposeConsents)
    this.model.purposeLegitimateInterests.empty()
    this.model.purposeLegitimateInterests.set(purposeLegitimateInterests)

    this.model.specialFeatureOptins.empty()
    this.model.specialFeatureOptins.set(specialFeatureOptins)

    this.model.consentScreen = consentScreen

    this.model.updated()

    if (created) {
      this.model.created = created
    }

    if (lastUpdated) {
      this.model.lastUpdated = lastUpdated
    }

    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  consentAll ({ consentScreen }) {
    this.model.setAll()
    this.model.consentScreen = consentScreen
    this.model.updated()
    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  dissentAll ({ consentScreen }) {
    this.model.unsetAll()
    this.model.setAllPurposeLegitimateInterests()
    this.model.setAllVendorLegitimateInterests()
    this.model.consentScreen = consentScreen
    this.model.updated()
    this.model = TCString.decode(TCString.encode(this.model))
    this.model.gvl = this.gvl
  }

  hasUserDissentedToAll () {
    return !this.model.specialFeatureOptins.size &&
      !this.model.purposeConsents.size &&
      !this.model.vendorConsents.size
  }

  hasUserConsentedToAll () {
    const vendorsWithPurposes = Object.keys(this.model.gvl.vendors).filter(id => this.model.gvl.vendors[id].purposes.length > 0).map(id => parseInt(id))
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const specialFeatureOptinIds = Object.keys(this.model.gvl.specialFeatures).map(id => parseInt(id))
    const hasConsentedToAllSpecialFeatureOptins = specialFeatureOptinIds.every(id => this.model.specialFeatureOptins.has(id))
    const hasConsentedToAllPurposes = purposesIds.every(id => this.model.purposeConsents.has(id))
    const hasConsentedToAllVendorPurposes = vendorsWithPurposes.every(id => this.model.vendorConsents.has(id))
    return hasConsentedToAllVendorPurposes && hasConsentedToAllPurposes && hasConsentedToAllSpecialFeatureOptins
  }

  consentedVendorCheck () {
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const purposeRestrictions = [3, 4].map(purposeId => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      return purposeRestriction
    })
    const model = TCString.decode(TCString.encode(this.model))
    model.gvl = this.gvl
    const vendorIdsDissentedByPurpose = new Set(purposesIds.filter(id => !model.purposeConsents.has(id)).map(id => Array.from(this.model.gvl.byPurposeVendorMap[id] && this.model.gvl.byPurposeVendorMap[id].consent || [])).flat())
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some(restriction => {
        return model.publisherRestrictions.vendorHasRestriction(id, restriction) && !model.purposeConsents.has(restriction.purposeId)
      })
    }
    const preferenceForIds = Array.from(this.model.gvl.vendorIds).map(id => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const vendorRestrictedToConsent = purposeRestrictions.some(restriction => model.publisherRestrictions.vendorHasRestriction(id, restriction))
      const hasConsented = (vendorRestrictedToConsent ? model.vendorConsents.has(id) : (model.vendorConsents.has(id) || model.vendorLegitimateInterests.has(id))) &&
        !vendorIdsDissentedByPurpose.has(id) &&
        !constrainedByFlexPurpose

      return {
        id,
        hasConsented,
        consentedTests: {
          consented: model.vendorConsents.has(id),
          legitimateInterest: model.vendorLegitimateInterests.has(id),
          vendorRestrictedToConsent,
          dissentedByPurpose: vendorIdsDissentedByPurpose.has(id),
          constrainedByFlexPurpose: constrainedByFlexPurpose
        }
      }
    })
    return preferenceForIds
  }

  consentedVendors () {
    return this.consentedVendorCheck().filter(({ hasConsented }) => hasConsented).map(({ id }) => id)
  }

  consentedVendorsDetails () {
    return this.consentedVendorCheck()
  }
}

/**
 * @typedef {Object} TcModelOpts
 * @property {GVL} gvl
 * @property {string} molGvlVersion
 * @property {TCModel} model
 * @property {Array<import('@mol-ads/tcf-core/lib/mjs/model/gvl/Vendor')>} specialPurposeOnlyDeclared
 */

